import { useState, useCallback, useContext, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useFeatureToggle } from 'hooks'
import {
  createMeeting,
  createBlankActivity,
  updateActivity,
  getActivities,
} from '../service/projects'
import {
  GeneratePlanningParams,
  AIPlanPayload,
  generateWeeklyDates,
  validatePlanningParams,
  getMeetingTitle,
  getMeetingTime,
  getMeetingDuration,
  getActivityTitle,
  getActivityPlanning,
  PLANNING_CONFIG,
} from '../utils/planningHelpers'
// Versão Legacy (main)
import { generateLessonPlanWithAI as generateLessonPlanWithAILegacy, AILessonPlanResponse } from '../service/aiLessonPlanService'
// Versão Arc (N8N direto)
import { generateLessonPlanWithAI as generateLessonPlanWithAIArc } from '../service/TEMP_n8nDirect'
import { transformAIContextToLessonPlanPayload, validateAIContextForLessonPlan } from '../utils/aiLessonPlanHelpers'
import { fetchSelectedProjectItems } from '../utils/projectItemsHelpers'
import { validatePayload } from '../utils/payloadValidator'
import { StepperContext } from '../context/StepperContext'
import { useAILessonProcessor, ProcessedMeeting } from './useAILessonProcessor'
import { useMeetingCreator } from './useMeetingCreator'

export type GenerationProgress = {
  currentStep: 'meetings' | 'activities' | 'idle' | 'ai_processing'
  current: number
  total: number
  percentage: number
  message?: string
}

export type GenerationResult = {
  success: boolean
  meetingsCreated: number
  activitiesCreated: number
  error?: string
}

export function useLessonPlanningGenerator() {
  const { t } = useTranslation()
  const arcNewProjectEnabled = useFeatureToggle('arc-mosaico-project')
  const { aiContext } = useContext(StepperContext)
  const { processAIResponse } = useAILessonProcessor()
  const { createSingleMeeting, createMeetingActivities } = useMeetingCreator()
  const [isGenerating, setIsGenerating] = useState(false)

  // Escolhe a função de geração baseado na feature flag
  const generateLessonPlanWithAI = arcNewProjectEnabled
    ? generateLessonPlanWithAIArc
    : generateLessonPlanWithAILegacy
  const [progress, setProgress] = useState<GenerationProgress>({
    currentStep: 'idle',
    current: 0,
    total: 0,
    percentage: 0,
  })

  const tRef = useRef(t)
  tRef.current = t

  const updateProgress = useCallback(
    (step: 'meetings' | 'activities', current: number, total: number) => {
      setProgress({
        currentStep: step,
        current,
        total,
        percentage: Math.round((current / total) * 100),
      })
    },
    []
  )

  const resetProgress = useCallback(() => {
    setProgress({
      currentStep: 'idle',
      current: 0,
      total: 0,
      percentage: 0,
    })
  }, [])

  const generate = useCallback(
    async (
      params: GeneratePlanningParams,
      aiPlan?: AIPlanPayload
    ): Promise<GenerationResult> => {
      const result: GenerationResult = {
        success: false,
        meetingsCreated: 0,
        activitiesCreated: 0,
      }

      try {
        setIsGenerating(true)
        validatePlanningParams(params)

        const { projectId, startDate, durationWeeks } = params

        // 🤖 PASSO 1: Chamar API de IA se o contexto estiver disponível
        let aiResponse: AILessonPlanResponse | null = null

        if (aiContext && validateAIContextForLessonPlan(aiContext)) {
          try {
            setProgress({
              currentStep: 'ai_processing',
              current: 1,
              total: 5,
              percentage: 20,
              message: tRef.current('new_project_page.ai_analyzing_context')
            })

            const selectedItems = await fetchSelectedProjectItems(projectId)

            setProgress({
              currentStep: 'ai_processing',
              current: 2,
              total: 5,
              percentage: 40,
              message: tRef.current('new_project_page.ai_preparing_items')
            })

            const aiPayload = transformAIContextToLessonPlanPayload(
              aiContext,
              durationWeeks,
              startDate,
              selectedItems
            )

            const validation = validatePayload(aiPayload)
            if (!validation.isValid) {
              console.error('❌ Payload inválido:', validation.errors)
              throw new Error(`Payload inválido: ${validation.errors.join(', ')}`)
            }

            setProgress({
              currentStep: 'ai_processing',
              current: 3,
              total: 5,
              percentage: 60,
              message: tRef.current('new_project_page.ai_generating_meetings')
            })

            aiResponse = await generateLessonPlanWithAI(aiPayload)

            setProgress({
              currentStep: 'ai_processing',
              current: 4,
              total: 5,
              percentage: 80,
              message: tRef.current('new_project_page.ai_processing_response')
            })
          } catch (aiError: any) {
            console.warn('⚠️ Erro ao chamar IA, continuando com geração padrão:', aiError.message)
          }
        }

        // 🏗️ PASSO 2: Processar resposta da IA e criar meetings/activities
        const processedMeetings = aiResponse ? processAIResponse(aiResponse) : null

        if (processedMeetings && processedMeetings.length > 0) {
          // ✨ FLUXO COM DADOS DA IA
          const createdDates: string[] = []

          // Criar cada meeting retornado pela IA
          for (let i = 0; i < processedMeetings.length; i++) {
            const processedMeeting = processedMeetings[i]
            updateProgress('meetings', i + 1, processedMeetings.length)

            await createSingleMeeting(projectId, processedMeeting)
            createdDates.push(processedMeeting.date)
            result.meetingsCreated++
          }

          // Buscar meetings criados do backend
          const activitiesData = await getActivities(projectId, 1)

          if (!activitiesData || !activitiesData.sessionMeetings) {
            throw new Error('Não foi possível buscar os encontros criados')
          }

          const createdMeetings = activitiesData.sessionMeetings.filter((meeting: any) =>
            createdDates.includes(meeting.date)
          )

          // Criar activities para cada meeting
          for (let i = 0; i < createdMeetings.length; i++) {
            const backendMeeting = createdMeetings[i]
            const processedMeeting = processedMeetings.find(
              pm => pm.date === backendMeeting.date
            )

            if (processedMeeting && processedMeeting.activities.length > 0) {
              const activitiesCreated = await createMeetingActivities(
                projectId,
                backendMeeting.id,
                processedMeeting.activities,
                (current, total) => {
                  const globalIndex = i * processedMeeting.activities.length + current
                  const globalTotal = processedMeetings.reduce(
                    (sum, m) => sum + m.activities.length,
                    0
                  )
                  updateProgress('activities', globalIndex, globalTotal)
                }
              )

              result.activitiesCreated += activitiesCreated
            }
          }
        } else {
          // 📝 FLUXO PADRÃO (Fallback quando não há dados da IA)
          const dates = generateWeeklyDates(startDate, durationWeeks)
          const createdDates: string[] = []

          for (let i = 0; i < dates.length; i++) {
            const index = i + 1
            updateProgress('meetings', index, dates.length)

            const meetingPayload = {
              project_id: projectId,
              date: dates[i],
              duration: getMeetingDuration(index, aiPlan),
              time: getMeetingTime(index, aiPlan),
              title: getMeetingTitle(index, aiPlan),
              enable_pendency_journal: PLANNING_CONFIG.ENABLE_PENDENCY_JOURNAL,
            }

            const response = await createMeeting(meetingPayload)

            if (response && response.alreadyMeetings && response.alreadyMeetings.length > 0) {
              throw new Error(
                `Já existe um encontro na data ${dates[i]}. Escolha uma data inicial diferente.`
              )
            }

            createdDates.push(dates[i])
            result.meetingsCreated++
          }

          const activitiesData = await getActivities(projectId, 1)

          if (!activitiesData || !activitiesData.sessionMeetings) {
            throw new Error('Não foi possível buscar os encontros criados')
          }

          const createdMeetings = activitiesData.sessionMeetings.filter((meeting: any) =>
            createdDates.includes(meeting.date)
          )

          for (let i = 0; i < createdMeetings.length; i++) {
            const index = i + 1
            const meeting = createdMeetings[i]

            updateProgress('activities', index, createdMeetings.length)

            const blankActivity = await createBlankActivity({
              session_meeting_id: meeting.id,
              project_id: projectId,
            })

            if (!blankActivity || !blankActivity.id) {
              throw new Error(`Falha ao criar atividade em branco ${index}`)
            }

            const activityPayload = {
              id: blankActivity.id,
              project_id: projectId,
              title: getActivityTitle(index, aiPlan),
              planning: getActivityPlanning(index, aiPlan),
              has_assessments: false,
              activity_lv4_items_attributes: [],
            }

            await updateActivity(blankActivity.id, activityPayload)
            result.activitiesCreated++
          }
        }

        result.success = true
        return result
      } catch (error: any) {
        result.error = error?.message || 'Erro inesperado ao gerar planejamento'
        return result
      } finally {
        setIsGenerating(false)
        resetProgress()
      }
    },
    [
      updateProgress,
      resetProgress,
      aiContext,
      processAIResponse,
      createSingleMeeting,
      createMeetingActivities,
    ]
  )

  return {
    generate,
    isGenerating,
    progress,
  }
}
